import { todayCalendarDate } from '@shared/dates/calendarDate';

import type { InsuranceInfo } from '../../../types/types';
import type {
  CoverageDisplayStatus,
  CreatePatientCoverageRequest,
  PatientCoverage,
} from '../types';

/**
 * Pure coverage helpers — no API/store imports, so tests can load the
 * real logic without pulling in the generated OpenAPI client (GDN-3068:
 * three suites previously re-implemented these in local mocks, which
 * stayed green while the real behaviour changed underneath).
 * Re-exported through `./hooks` and the feature barrels.
 */

export const normalizeCoverageKind = (kind?: string | null): string =>
  kind?.trim().toLowerCase().replace('-', '_') || 'insurance';

/**
 * Whether a coverage ROW is a self-pay row. Contract phase (GDN-3177):
 * this describes what a row is — it must never answer "is this patient
 * self-pay"; that is `is_self_pay` on the patient/response, exclusively.
 * Kept for labeling rows that still exist (history, pre-retire data).
 */
export const isSelfPayCoverage = (coverage?: Partial<PatientCoverage> | null): boolean =>
  normalizeCoverageKind(coverage?.kind) === 'self_pay';

/**
 * Read the patient-level `is_self_pay` flag off a payload. The sole
 * source of self-pay-ness since the GDN-3177 contract phase — coverage
 * rows no longer imply designation. Absent (a cached response predating
 * the field) means "unknown" and renders as not-designated.
 */
export const getPatientSelfPayFlag = (payload?: unknown): boolean | undefined => {
  if (!payload || typeof payload !== 'object' || !('is_self_pay' in payload)) {
    return undefined;
  }
  const flag = (payload as { is_self_pay?: boolean | null }).is_self_pay;
  return typeof flag === 'boolean' ? flag : undefined;
};

export const getCoverageDisplayLabel = (
  coverage?: Partial<PatientCoverage & { display_label?: string | null }> | null,
): string | null => {
  if (!coverage) return null;
  if (isSelfPayCoverage(coverage)) return 'Self-pay';
  return coverage.display_label ?? coverage.payer_name ?? null;
};

/**
 * Helper to convert empty/falsy values to null (for API)
 */
const toNullable = <T>(value: T | undefined | null | ''): T | null =>
  value === '' || value === undefined ? null : (value);

/**
 * Maps insurance data (legacy InsuranceInfo or PatientCoverage) to a coverage request format.
 * Use this when creating or updating coverages to ensure consistent field mapping.
 *
 * @param coverageOrder - 1 for primary, 2 for secondary, 3 for tertiary
 * @param insurance - The insurance data to map (can be legacy or new format)
 * @returns A properly formatted coverage request object
 */
export function mapInsuranceToCoverageRequest(
  coverageOrder: number,
  insurance: InsuranceInfo | PatientCoverage,
): CreatePatientCoverageRequest {
  // Helper to check if data is new format
  const isPatientCoverage = (data: InsuranceInfo | PatientCoverage): data is PatientCoverage =>
    'patient_insurance_coverage_id' in data;

  // Legacy format stores the subscriber name combined in insured_name;
  // split it so subscriber identity is never dropped on save (GDN-2317).
  // Split on whitespace runs — OCR'd names often carry double spaces.
  const legacyInsuredName =
    'insured_name' in insurance ? insurance.insured_name?.trim() || '' : '';
  const legacyNameParts = legacyInsuredName.split(/\s+/).filter(Boolean);
  const legacyFirstName = legacyNameParts[0] || null;
  const legacyLastName = legacyNameParts.slice(1).join(' ') || null;

  return {
    coverage_order: coverageOrder,
    kind: isPatientCoverage(insurance) ? normalizeCoverageKind(insurance.kind) : 'insurance',
    payer_id: toNullable(insurance.payer_id),
    payer_name: toNullable(insurance.payer_name),
    plan_name: toNullable('plan_name' in insurance ? insurance.plan_name : null),
    member_id: toNullable(
      'member_id' in insurance
        ? insurance.member_id
        : 'policy_number' in insurance
          ? insurance.policy_number
          : null,
    ),
    group_number: toNullable(insurance.group_number),
    claim_filing_code: toNullable(
      'claim_filing_code' in insurance ? insurance.claim_filing_code : null,
    ),
    relationship_to_subscriber:
      toNullable(
        'relationship_to_subscriber' in insurance
          ? insurance.relationship_to_subscriber
          : 'relationship_to_insured' in insurance
            ? insurance.relationship_to_insured
            : null,
      ) || 'self',
    consent_on_file:
      ('consent_on_file' in insurance
        ? insurance.consent_on_file
        : 'authorization_on_file' in insurance
          ? insurance.authorization_on_file
          : undefined) ?? undefined,
    subscriber_first_name: toNullable(
      'subscriber_first_name' in insurance ? insurance.subscriber_first_name : legacyFirstName,
    ),
    subscriber_last_name: toNullable(
      'subscriber_last_name' in insurance ? insurance.subscriber_last_name : legacyLastName,
    ),
    subscriber_dob: toNullable(
      'subscriber_dob' in insurance
        ? insurance.subscriber_dob
        : 'insured_dob' in insurance
          ? insurance.insured_dob
          : null,
    ),
    subscriber_sex: toNullable(
      'subscriber_sex' in insurance
        ? insurance.subscriber_sex
        : 'insured_sex' in insurance
          ? insurance.insured_sex
          : null,
    ),
    subscriber_address: toNullable(
      'subscriber_address' in insurance
        ? insurance.subscriber_address
        : 'insured_address' in insurance
          ? insurance.insured_address
          : null,
    ),
    // Coverage date range (GDN-2223) — patient_coverage-only fields, no
    // legacy InsuranceInfo equivalent. effective_date may also arrive from
    // the insurance-card OCR flow; either source flows through here.
    effective_date: toNullable('effective_date' in insurance ? insurance.effective_date : null),
    termination_date: toNullable(
      'termination_date' in insurance ? insurance.termination_date : null,
    ),
  };
}

/**
 * Backend-derived presentation status, with a client-side fallback for
 * cached responses that predate the field (GDN-2411).
 */
export function getCoverageDisplayStatus(
  coverage: Partial<PatientCoverage>,
): CoverageDisplayStatus {
  if (coverage.display_status) {
    return coverage.display_status as CoverageDisplayStatus;
  }
  if (coverage.status === 'cancelled') return 'cancelled';
  if (coverage.status === 'pending') return 'pending';
  // Preserve the existing UTC fallback; server display_status remains authoritative.
  const today = todayCalendarDate('UTC');
  if (coverage.effective_date && coverage.effective_date > today) return 'future';
  if (coverage.termination_date && coverage.termination_date < today) return 'expired';
  if (coverage.status === 'expired' && !coverage.termination_date) return 'expired';
  return 'active';
}

/**
 * Helper to determine if a coverage needs subscriber information
 */
export function needsSubscriberInfo(coverage: Partial<PatientCoverage>): boolean {
  return coverage.relationship_to_subscriber !== 'self' && !!coverage.relationship_to_subscriber;
}

/**
 * Helper to get a display name for coverage order
 */
export function getCoverageOrderLabel(order: number): string {
  switch (order) {
    case 1:
      return 'Primary';
    case 2:
      return 'Secondary';
    case 3:
      return 'Tertiary';
    default:
      return `Coverage ${order}`;
  }
}
